import { useState, type CSSProperties } from "react";
import { cn } from "@/lib/cn";

/** 지름(px). 쓰는 자리를 이름으로 고른다 — 화면 코드가 크기를 따로 덮어쓰지 않게. */
const SIZE = {
  /** 달력 막대 안 — 20px 줄에 들어가는 가장 작은 표지 */
  "2xs": 16,
  /** 글줄 안 — 칩·표·목록에서 이름 앞, 겹쳐 쌓는 더미 */
  xs: 20,
  /** 상단 프로필 버튼·코멘트 */
  sm: 28,
  /** 목록 한 줄의 머리 — 직원 목록·알림 */
  md: 36,
  /** 설정의 내 정보 */
  lg: 64,
  /** 직원 상세의 프로필 */
  xl: 96,
} as const;

export type AvatarSize = keyof typeof SIZE;

/** 머리글자 크기와 테두리 두께는 지름에 대한 비율로 정한다 — 어느 크기에서도 같은 모양이다. */
const TEXT_RATIO = 0.42;
const RING_RATIO = 0.08;

/**
 * 사람 표지 — 지름 하나만 고르면 머리글자 크기와 테두리 두께가 같은 비율로 따라온다. 프로필을
 * 그리는 곳은 모두 이것을 쓴다(크기를 className 으로 덮어쓰지 않는다).
 *
 * 프로필 사진(관리자가 직원 관리에서 올린다)이 있으면 `src` 로 넘긴다 — 같은 원에 사진을 채우고,
 * 없거나 못 읽으면 이름 첫 글자를 진한 원 위에 둔다.
 *
 * `ring` 은 겹쳐 쌓을 때 이웃과 가르는 바탕색 테두리다.
 */
export function Avatar({
  name,
  size = "sm",
  src,
  ring = false,
  className,
}: {
  name: string;
  size?: AvatarSize;
  /** 프로필 사진 주소(응답의 avatar_url). 있으면 머리글자 대신 쓴다 */
  src?: string | null;
  ring?: boolean;
  /** 자리 잡기(여백·정렬)만. 크기와 글자는 size 가 정한다 */
  className?: string;
}) {
  // 못 읽은 주소를 기억한다 — 사진을 바꿔 주소가 달라지면 다시 읽어 본다.
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  const diameter = SIZE[size];
  const style: CSSProperties = {
    width: diameter,
    height: diameter,
    fontSize: Math.round(diameter * TEXT_RATIO * 10) / 10,
    boxShadow: ring
      ? `0 0 0 ${Math.max(1, Math.round(diameter * RING_RATIO))}px var(--color-clay-surface)`
      : undefined,
  };

  return (
    <span
      aria-hidden
      style={style}
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-clay-surface-inverse leading-none font-semibold text-clay-text-inverse",
        className
      )}
    >
      {src && src !== brokenSrc ? (
        <img
          src={src}
          alt=""
          className="size-full object-cover"
          onError={() => setBrokenSrc(src)}
        />
      ) : (
        name.charAt(0)
      )}
    </span>
  );
}
